import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Dropdown,
  Drawer,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BgColorsOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SettingOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  getAdminClients,
  getAvailableModules,
  getAdminFormGroups,
  updateAdminFormGroup,
  getDetailConfig,
  saveDetailConfig,
  getModuleDetailPreview,
  getAvailableCollections,
  getCollectionFields,
  getDetailDefaults,
  saveDetailDefaults,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
// Read-only import on purpose — this screen only ever ASKS whether a field
// carries a uniqueness rule. Authoring lives in Form Groups.
import { hasUniqueValidation, uniqueValidationOf } from './uniqueValidationConfig';
import {
  getDefaults,
  applyDefaults,
  loadDetailDefaults,
  BUILT_IN_DEFAULTS,
} from '../../../services/detailDefaults';
import AppCard from '../../../components/cards/AppCard';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import FieldValue from '../../../components/detail/fieldRenderEngine';
import { BUILT_IN_EMPTY_TEXTS } from '../../../components/detail/emptyText';
import { listTimeZones, tzLabel, tzShortLabel, formatAppDateTime } from '../../../services/timezone';

// A fixed instant for the live format preview, so the sample never changes
// while an admin is typing and cannot be mistaken for real data.
const SAMPLE_INSTANT = '2026-08-06T13:33:00.000Z';
import { recordValues, detailFieldLabel } from '../../../components/detail/detailVisibility';
import { CONDITION_OPERATORS, operatorNeedsValue } from '../../../components/form/conditionOperators';
import { RENDER_TYPES, DOCUMENT_MODES, TEXT_TRANSFORMS } from '../../../components/detail/renderConfig';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// Detail editor metadata changes rarely during an admin session. Reusing these
// requests makes repeated field edits open immediately instead of refetching.
let collectionsRequest;
const collectionFieldsRequests = new Map();

function loadCollectionsCached() {
  if (!collectionsRequest) {
    collectionsRequest = getAvailableCollections().catch((error) => {
      collectionsRequest = undefined;
      throw error;
    });
  }
  return collectionsRequest;
}

function loadCollectionFieldsCached(collection) {
  if (!collectionFieldsRequests.has(collection)) {
    collectionFieldsRequests.set(collection, getCollectionFields(collection).catch((error) => {
      collectionFieldsRequests.delete(collection);
      throw error;
    }));
  }
  return collectionFieldsRequests.get(collection);
}

// OTP-gated config actions for the detail-view config, mirroring Form Groups.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all detail config',  confirmLabel: 'Reset All',       danger: true,
    description: 'Clears every detail-view override for all modules in this workspace, so the detail view falls back to the plain form-group fields.' },
  save:    { action: 'save',    title: 'Save current as default',  confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current detail config for all modules as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',    confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current detail config for all modules with the last saved default.' },
};


// capitalize is a tri-state (*bool on the backend): unset → default behaviour,
// true → force title-case, false → leave the raw value alone.
const CAP_OPTIONS = [
  { label: 'Default',        value: 'default' },
  { label: 'Title Case On',  value: 'on' },
  { label: 'Off (raw text)', value: 'off' },
];
const capToValue = (c) => (c === true ? 'on' : c === false ? 'off' : 'default');
const valueToCap = (v) => (v === 'on' ? true : v === 'off' ? false : undefined);

// hideGroup lives on the SAME formGroupConfig group document the Form Groups
// admin screen edits — there is no separate detail-side flag, so toggling it
// here and toggling it in Form Groups always stay in sync automatically.
const truthy = (value) => value === true || value === 1 || value === '1';

// Flatten the module's form-groups into a de-duplicated field universe that the
// detail config overrides sit on top of.
function flattenFields(groups) {
  const seen = new Set();
  const out = [];
  for (const g of groups ?? []) {
    for (const f of g.fields ?? []) {
      if (!f.field || seen.has(f.field)) continue;
      seen.add(f.field);
      out.push({
        key: f.field,
        label: f.label ?? f.field,
        type: f.type ?? 'text',
        group: g.label ?? g.name,
        // Carried through READ-ONLY. Detail Groups is display configuration; it
        // never authors, edits or executes validation. Keeping `validations` on
        // the flattened field is what lets this screen SHOW that a field is
        // configured unique in Form Groups (and what stops a future flatten
        // consumer from writing a field back without it). `key` above stays the
        // raw form field key, so a label customised here can never change the
        // key uniqueness is validated against.
        validations: Array.isArray(f.validations) ? f.validations : [],
        lookupDefaults: f.lookupCollection ? {
          lookupCollection: f.lookupCollection,
          displayField: f.displayField ?? '',
          displayField2: f.displayField2 ?? '',
          valueField: f.valueField || '_id',
        } : {},
      });
    }
  }
  return out;
}

// Strip an override down to only the keys that actually change something, so the
// saved config stays minimal (and a field with no overrides is dropped).
function cleanFieldCfg(field, c) {
  if (!c) return null;
  const out = { field };
  if (c.label) out.label = c.label;
  if (c.prefix) out.prefix = c.prefix;
  if (c.suffix) out.suffix = c.suffix;
  if (c.color) out.color = c.color;
  if (c.capitalize === true || c.capitalize === false) out.capitalize = c.capitalize;
  if (c.hidden === true || c.hidden === false) out.hidden = c.hidden;
  // Detail-only hide — kept separate from `hidden` (which the save flow mirrors
  // onto the form field's `show`); this one never touches the form.
  if (c.hideInDetail === true) out.hideInDetail = true;
  // Hide-when-empty. `hideWhenEmpty` and `hideWhenEmptyInDetail` are ALIASES —
  // components/detail/detailEmptiness.js ORs them — so the editor exposes one
  // switch rather than two controls that fight over the same behaviour. The
  // switch writes the canonical `hideWhenEmpty`; a config seeded with the
  // longer alias is preserved verbatim while the switch stays on, so a
  // wire-* seeder's document is never silently rewritten by an unrelated save.
  if (c.hideWhenEmpty === true || c.hideWhenEmptyInDetail === true) {
    out.hideWhenEmpty = true;
    if (c.hideWhenEmptyInDetail === true) out.hideWhenEmptyInDetail = true;
  }
  // Zero-counts-as-empty for THIS field only (detailEmptiness.hidesWhenZero).
  // Its own switch rather than a mode on the one above: it is self-sufficient
  // (0 AND blank both hide), so an admin who wants "only show the range end
  // when there IS one" flips one thing, and a field that opts in can never be
  // half-configured by a hideWhenEmpty that was left off. Persisted only when
  // ON, like every other key here.
  if (c.hideWhenZero === true) out.hideWhenZero = true;
  // Detail-only opt-out of the field's Conditional Label. Only persisted when
  // switched ON, so a field that simply follows the rule stays absent from the
  // saved config (same minimal-config rule as every other key here).
  if (c.ignoreLabelWhenInDetail === true) out.ignoreLabelWhenInDetail = true;
  // Detail-authored conditional labels. Half-typed rows are dropped rather than
  // saved: a rule with no watch field or no label can never match anything, and
  // keeping it would make the config lie about what is configured.
  const labelWhen = (c.labelWhen ?? [])
    .filter((rule) => String(rule?.field ?? '').trim() && String(rule?.label ?? '').trim())
    .map((rule) => {
      const cleaned = {
        field: String(rule.field).trim(),
        operator: rule.operator || 'eq',
        label: String(rule.label).trim(),
      };
      // Unary operators (truthy/falsy/notEmpty) carry no value — drop any left
      // behind by switching the operator, so it can't resurface later.
      if (operatorNeedsValue(cleaned.operator) && rule.value != null && rule.value !== '') {
        cleaned.value = String(rule.value);
      }
      return cleaned;
    });
  if (labelWhen.length) out.labelWhen = labelWhen;
  // Render type is the single source of truth — migrate the legacy
  // documentPreview / displayAs flags into it on save.
  const rt = c.renderType
    || (c.documentPreview ? 'document' : c.displayAs === 'tag' ? 'tag' : c.displayAs === 'text' ? 'text' : '');
  if (rt && rt !== 'auto') out.renderType = rt;
  const ro = {};
  for (const [k, v] of Object.entries(c.renderOptions ?? {})) {
    if (v !== undefined && v !== '' && v !== false) ro[k] = v;
  }
  if (Object.keys(ro).length) out.renderOptions = ro;
  if (c.lookupCollection) {
    out.lookupCollection = c.lookupCollection;
    out.displayField = c.displayField || '';
    if (c.displayField2) out.displayField2 = c.displayField2;
    out.valueField = c.valueField || '_id';
  }
  return Object.keys(out).length > 1 ? out : null;
}

function cleanMerges(merges) {
  return (merges ?? [])
    .filter((m) => m.key && (m.fields ?? []).length)
    .map((m) => {
      const out = { key: m.key, label: m.label || m.key, fields: m.fields };
      if (m.template) out.template = m.template;
      if (m.valueMaps && Object.keys(m.valueMaps).length) out.valueMaps = m.valueMaps;
      if (m.separator) out.separator = m.separator;
      if (m.prefix) out.prefix = m.prefix;
      if (m.suffix) out.suffix = m.suffix;
      if (m.color) out.color = m.color;
      if (m.capitalize === true || m.capitalize === false) out.capitalize = m.capitalize;
      if (m.group) out.group = m.group;
      if (m.hideSources === false) out.hideSources = false;
      return out;
    });
}

function fieldShowToHidden(field) {
  return Number(field?.show ?? 1) === 0;
}

function syncGroupsWithDetailVisibility(groups, fieldCfg) {
  const changed = [];
  const nextGroups = (groups ?? []).map((group) => {
    let groupChanged = false;
    const fields = (group.fields ?? []).map((field) => {
      const cfg = fieldCfg[field.field];
      if (!cfg || cfg.hidden === undefined) return field;

      const nextShow = cfg.hidden ? 0 : 1;
      if (Number(field.show ?? 1) === nextShow) return field;

      groupChanged = true;
      return { ...field, show: nextShow };
    });

    const nextGroup = groupChanged ? { ...group, fields } : group;
    if (groupChanged) changed.push(nextGroup);
    return nextGroup;
  });

  return { nextGroups, changed };
}

// ── Colour input (text + native swatch) ─────────────────────────────────────────
function ColorInput({ value, onChange }) {
  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        size="small"
        placeholder="#0053a5"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="fg-mono-input"
        prefix={<BgColorsOutlined style={{ color: value || '#bbb' }} />}
      />
      <input
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(value ?? '') ? value : '#0053a5'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 32, border: '1px solid #d9d9d9', borderLeft: 0, cursor: 'pointer', padding: 0 }}
        aria-label="Pick colour"
      />
    </Space.Compact>
  );
}

// ── Conditional Label editor ─────────────────────────────────────────────────
// Authors the rules that decide what this field is CALLED for a given record —
// e.g. a submission's rate reads "Employer Rate" when contractType is C2C and
// "Candidate Rate" otherwise. Pick the field to watch, the value to match, and
// the label to show; rules are evaluated top-down and the first match wins, so
// more than two states is just more rows.
//
// Only the displayed label changes — never the field key, the stored value, or
// anything the add/edit forms do.
function ConditionalLabelEditor({
  fieldMeta, rules, ignore, fieldOptions, onChangeRules, onChangeIgnore,
}) {
  const list = Array.isArray(rules) ? rules : [];
  const inherited = fieldMeta?.labelWhen;
  // With no rules of its own the field follows the form's Conditional Label —
  // show that, so "inherited" never looks like "nothing configured".
  const isInheriting = list.length === 0 && Boolean(inherited?.field && inherited?.label);

  const update = (index, patch) => onChangeRules(
    list.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
  );
  const remove = (index) => onChangeRules(list.filter((_, i) => i !== index));
  const add = () => onChangeRules([...list, { field: '', operator: 'eq', value: '', label: '' }]);
  // Start from the inherited rule so overriding is an edit, not a retype.
  const addFromInherited = () => onChangeRules([{
    field: inherited.field,
    operator: inherited.operator ?? 'eq',
    value: inherited.value ?? '',
    label: inherited.label,
  }]);

  return (
    <div className="fg-form-section" style={{ marginTop: 14, marginBottom: 0 }}>
      <div className="fg-form-section-label">Conditional Label</div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        Show a different label depending on another field&apos;s value on the record.
        The first matching rule wins; if none match, the label above (
        <b>{fieldMeta?.label}</b>) is used. Detail view only — the add/edit forms are
        unaffected.
      </Text>

      {isInheriting && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message={`Inheriting from Form Groups: "${inherited.label}" when ${inherited.field} ${inherited.operator ?? 'eq'} ${inherited.value ?? ''}`}
          description={(
            <Button size="small" type="link" style={{ padding: 0 }} onClick={addFromInherited}>
              Override it here
            </Button>
          )}
        />
      )}

      {list.map((rule, i) => {
        const needsValue = operatorNeedsValue(rule.operator);
        return (
          <div key={i} className="fg-field-meta-row" style={{ marginBottom: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1.4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>When field</Text>
              {/* mode="tags" + maxCount=1: pick a known field, or type a key the
                  flattened list does not carry (e.g. a recordMeta key). */}
              <Select
                size="small"
                showSearch
                mode="tags"
                maxCount={1}
                style={{ width: '100%' }}
                placeholder="e.g. contractType"
                optionFilterProp="label"
                options={fieldOptions}
                value={rule.field ? [rule.field] : []}
                onChange={(v) => update(i, { field: String(v[v.length - 1] ?? '').trim() })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Is</Text>
              <Select
                size="small"
                style={{ width: '100%' }}
                options={CONDITION_OPERATORS}
                value={rule.operator ?? 'eq'}
                onChange={(v) => update(i, { operator: v })}
              />
            </div>
            {needsValue && (
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Value</Text>
                <Input
                  size="small"
                  placeholder="C2C"
                  value={rule.value ?? ''}
                  onChange={(e) => update(i, { value: e.target.value })}
                  className="fg-mono-input"
                />
              </div>
            )}
            <div style={{ flex: 1.2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Show label</Text>
              <Input
                size="small"
                placeholder="Employer Rate"
                value={rule.label ?? ''}
                onChange={(e) => update(i, { label: e.target.value })}
              />
            </div>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
          </div>
        );
      })}

      <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={add}>
        Add rule
      </Button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
        <Switch size="small" checked={!ignore} onChange={(v) => onChangeIgnore(!v)} />
        <Text style={{ fontSize: 13 }}>Use conditional labels in the detail view</Text>
      </div>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
        Turn off to always show the static label here, ignoring both the rules above and
        the one from Form Groups.
      </Text>
    </div>
  );
}

// ── Field Override Drawer ────────────────────────────────────────────────────────
// moduleFieldOptions = every field on the module, offered as the "When field"
// choices for conditional labels. Distinct from the local `fieldOptions` below,
// which lists the columns of a LOOKUP collection.
function FieldOverrideDrawer({ open, fieldMeta, value, moduleFieldOptions, onClose, onSave }) {
  // The parent keys this component per field, so it remounts on every open —
  // a useState initializer seeds the draft (no prop→state effect needed).
  const [draft, setDraft] = useState(() => ({ ...(value ?? {}) }));
  const [collections, setCollections] = useState([]);
  const [collFields, setCollFields] = useState([]);
  const [activeSection, setActiveSection] = useState('display');

  useEffect(() => {
    if (!open || activeSection !== 'lookup') return undefined;
    let cancelled = false;
    loadCollectionsCached()
      .then((c) => { if (!cancelled) setCollections(c.map((x) => (typeof x === 'string' ? x : x.name ?? x.value ?? '')).filter(Boolean)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeSection, open]);

  useEffect(() => {
    if (!open || activeSection !== 'lookup' || !draft.lookupCollection) return undefined;
    let cancelled = false;
    loadCollectionFieldsCached(draft.lookupCollection)
      .then((f) => { if (!cancelled) setCollFields(f.map((x) => (typeof x === 'string' ? x : x.value ?? x.field ?? x.label ?? '')).filter(Boolean)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeSection, open, draft.lookupCollection]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Clearing collFields in the handler (not an effect) keeps suggestions fresh.
  const setCollection = (v) => { setCollFields([]); setDraft((d) => ({ ...d, lookupCollection: v })); };
  const collOptions = collections.map((c) => ({ value: c }));
  const fieldOptions = collFields.map((c) => ({ value: c }));

  // Render type drives the detail view. Derive from legacy flags for old configs.
  const renderOpts = draft.renderOptions ?? {};
  const setOpt = (k, v) => set('renderOptions', { ...renderOpts, [k]: v || undefined });
  const effectiveRenderType = draft.renderType
    || (draft.documentPreview ? 'document' : draft.displayAs === 'tag' ? 'tag' : draft.displayAs === 'text' ? 'text' : 'auto');
  const isTagLike = ['tag', 'tags', 'chip', 'badge'].includes(effectiveRenderType);
  const isTransformable = ['text', 'tag', 'tags', 'chip', 'badge', 'link', 'email', 'phone'].includes(effectiveRenderType);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal detail-field-editor-modal"
      title={
        <span className="fg-drawer-title">
          <EditOutlined /> {fieldMeta?.label}
          <code className="fg-field-key-hint" style={{ marginLeft: 8 }}>{fieldMeta?.key}</code>
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => onSave(draft)}>Apply</Button>
        </div>
      }
    >
      <div className="detail-editor-nav" role="tablist" aria-label="Field settings">
        <Button type={activeSection === 'display' ? 'primary' : 'text'} onClick={() => setActiveSection('display')}>
          Display
        </Button>
        <Button type={activeSection === 'lookup' ? 'primary' : 'text'} onClick={() => setActiveSection('lookup')}>
          Reference lookup
        </Button>
      </div>

      {activeSection === 'display' && <div className="fg-form-section detail-editor-panel">
        <div className="fg-form-section-label">Display</div>

        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Label override</Text>
          <Input size="small" placeholder={fieldMeta?.label} value={draft.label ?? ''} onChange={(e) => set('label', e.target.value)} />
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Prefix</Text>
            <Input size="small" placeholder="e.g. $" value={draft.prefix ?? ''} onChange={(e) => set('prefix', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Suffix</Text>
            <Input size="small" placeholder="e.g. yrs" value={draft.suffix ?? ''} onChange={(e) => set('suffix', e.target.value)} />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Colour</Text>
            <ColorInput value={draft.color} onChange={(v) => set('color', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Capitalize</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              options={CAP_OPTIONS}
              value={capToValue(draft.capitalize)}
              onChange={(v) => set('capitalize', valueToCap(v))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!draft.hidden} onChange={(v) => set('hidden', v)} />
          <Text style={{ fontSize: 13 }}>Hide this field everywhere (detail view and the add/edit form)</Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <Switch size="small" checked={!!draft.hideInDetail} onChange={(v) => set('hideInDetail', v)} />
          <Text style={{ fontSize: 13 }}>Hide in the detail view only (keep it in the add/edit form)</Text>
        </div>

        {/* One switch, both stored keys — see cleanFieldCfg for why. Turning it OFF
            clears the alias too, otherwise the field would keep hiding itself with
            no switch left showing that it does. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <Switch
            size="small"
            checked={!!draft.hideWhenEmpty || !!draft.hideWhenEmptyInDetail}
            onChange={(v) => setDraft((d) => ({
              ...d,
              hideWhenEmpty: v,
              hideWhenEmptyInDetail: v ? d.hideWhenEmptyInDetail : false,
            }))}
          />
          <Text style={{ fontSize: 13 }}>
            Hide when empty — drop this field (and a combined block left with nothing
            to show) instead of printing a heading over a dash
          </Text>
        </div>

        {/* Zero counts as empty, for this field only. Deliberately NOT global:
            a rate of 0, a count of 0 and a 0% are real values everywhere else,
            so only the field an admin points at here treats 0 as nothing. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <Switch
            size="small"
            checked={!!draft.hideWhenZero}
            onChange={(v) => set('hideWhenZero', v)}
          />
          <Text style={{ fontSize: 13 }}>
            Treat 0 as empty — hide this field when its value is 0 or blank (e.g. the
            end of a rate range that was never filled in, so &quot;92 - 0&quot; shows as
            just &quot;92&quot;)
          </Text>
        </div>

        <ConditionalLabelEditor
          fieldMeta={fieldMeta}
          rules={draft.labelWhen}
          ignore={draft.ignoreLabelWhenInDetail}
          fieldOptions={moduleFieldOptions}
          onChangeRules={(v) => set('labelWhen', v)}
          onChangeIgnore={(v) => set('ignoreLabelWhenInDetail', v)}
        />

        <div style={{ marginTop: 14 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Render Type</Text>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={effectiveRenderType}
            onChange={(v) => set('renderType', v)}
            options={RENDER_TYPES}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            How this field is displayed in the detail view — entirely config-driven.
          </Text>
        </div>

        {/* Per-field empty text. Overrides the tenant-wide wording (Display
            Defaults → Empty-value text by kind) for THIS field only, e.g.
            "No LinkedIn profile added" rather than a generic "Not provided". */}
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Empty text</Text>
          <Input
            size="small"
            placeholder={BUILT_IN_EMPTY_TEXTS[effectiveRenderType] ?? BUILT_IN_EMPTY_TEXTS.default}
            value={renderOpts.emptyText ?? ''}
            onChange={(e) => setOpt('emptyText', e.target.value)}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            What to show when this particular field is empty — for example &quot;No LinkedIn
            profile added&quot;. Leave blank to use the wording set for its type in Display
            Defaults.
          </Text>
        </div>

        {/* Render-type-specific options */}
        {effectiveRenderType === 'document' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Document mode</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={renderOpts.documentMode || 'preview'}
              onChange={(v) => setOpt('documentMode', v)}
              options={DOCUMENT_MODES}
            />
          </div>
        )}
        {isTransformable && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Text transform</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={renderOpts.textTransform || 'none'}
              onChange={(v) => setOpt('textTransform', v === 'none' ? undefined : v)}
              options={TEXT_TRANSFORMS}
            />
          </div>
        )}
        {isTagLike && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tag colour</Text>
            <Input size="small" placeholder="geekblue / #0053a5" value={renderOpts.tagColor ?? ''} onChange={(e) => setOpt('tagColor', e.target.value)} className="fg-mono-input" />
          </div>
        )}
        {effectiveRenderType === 'date' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Date format (dayjs tokens)</Text>
            <Input size="small" placeholder="DD/MM/YYYY" value={renderOpts.dateFormat ?? ''} onChange={(e) => setOpt('dateFormat', e.target.value)} className="fg-mono-input" />
          </div>
        )}
        {/* Phone — which key on the RECORD carries the dialling code. Without this
            the renderer can only guess from sibling names (countryCode /
            phoneCountryCode / …) and then falls back to the tenant default, which
            is exactly how every candidate came out displayed as "+1". Dotted paths
            are allowed, e.g. contact.countryCode. */}
        {effectiveRenderType === 'phone' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Country-code field on the record</Text>
            <Input
              size="small"
              placeholder="e.g. countryCode or contact.countryCode"
              value={renderOpts.countryCodeField ?? ''}
              onChange={(e) => setOpt('countryCodeField', e.target.value)}
              className="fg-mono-input"
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Leave blank to auto-detect a sibling code key. Nothing is prefixed when the
              record carries no code and no global fallback is set.
            </Text>
          </div>
        )}
        {(effectiveRenderType === 'currency' || effectiveRenderType === 'budget') && (
          <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Currency symbol</Text>
              <Input size="small" placeholder="$ / ₹ / £" value={renderOpts.currencySymbol ?? ''} onChange={(e) => setOpt('currencySymbol', e.target.value)} />
            </div>
            {effectiveRenderType === 'budget' && (
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Unit (e.g. Monthly)</Text>
                <Input size="small" placeholder="Monthly" value={renderOpts.unit ?? ''} onChange={(e) => setOpt('unit', e.target.value)} />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!renderOpts.showEmpty} onChange={(v) => setOpt('showEmpty', v)} />
          <Text style={{ fontSize: 13 }}>Show empty values</Text>
        </div>
      </div>}

      {activeSection === 'lookup' && <div className="fg-form-section detail-editor-panel">
        <div className="fg-form-section-label">Reference Lookup (id → name)</div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          For fields that store a foreign-key id. The detail API resolves the name from the
          target collection and returns <code>{'{ id, value }'}</code>.
        </Text>

        <div style={{ marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Collection</Text>
          <AutoComplete
            size="small"
            style={{ width: '100%' }}
            options={collOptions}
            placeholder="e.g. vendors"
            value={draft.lookupCollection ?? ''}
            onChange={setCollection}
            filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
            className="fg-mono-input"
          />
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Display Field</Text>
            <AutoComplete
              size="small"
              style={{ width: '100%' }}
              options={fieldOptions}
              placeholder="e.g. vendorCompanyName"
              value={draft.displayField ?? ''}
              onChange={(v) => set('displayField', v)}
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Display Field 2</Text>
            <AutoComplete
              size="small"
              style={{ width: '100%' }}
              options={fieldOptions}
              placeholder="e.g. lastName"
              value={draft.displayField2 ?? ''}
              onChange={(v) => set('displayField2', v)}
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Value Field (FK)</Text>
            <Input size="small" placeholder="_id" value={draft.valueField ?? ''} onChange={(e) => set('valueField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
      </div>}
    </Modal>
  );
}

// ── Merge Drawer ──────────────────────────────────────────────────────────────
// flattenValueMaps turns the saved { field: { raw: display } } shape into flat
// editor rows; buildValueMaps does the reverse.
function flattenValueMaps(valueMaps) {
  const rows = [];
  Object.entries(valueMaps ?? {}).forEach(([field, mapping]) => {
    Object.entries(mapping ?? {}).forEach(([from, to]) => rows.push({ field, from, to }));
  });
  return rows;
}

function buildValueMaps(rows) {
  const out = {};
  for (const r of rows ?? []) {
    if (!r.field || !r.from) continue;
    out[r.field] = { ...(out[r.field] ?? {}), [r.from]: r.to ?? '' };
  }
  return out;
}

function MergeDrawer({ open, merge, fieldOptions, groupOptions, onClose, onSave }) {
  // Parent keys this per merge → remounts on open, so the initializer seeds it.
  const [draft, setDraft] = useState(() => ({
    key: '', label: '', fields: [], separator: ' - ', template: '',
    prefix: '', suffix: '', color: '', capitalize: undefined,
    group: '', hideSources: true,
    ...(merge ?? {}),
    mapRows: flattenValueMaps(merge?.valueMaps),
  }));

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateRow = (i, patch) => setDraft((d) => {
    const rows = [...(d.mapRows ?? [])];
    rows[i] = { ...rows[i], ...patch };
    return { ...d, mapRows: rows };
  });
  const removeRow = (i) => setDraft((d) => ({ ...d, mapRows: (d.mapRows ?? []).filter((_, idx) => idx !== i) }));
  const addRow = () => setDraft((d) => ({
    ...d, mapRows: [...(d.mapRows ?? []), { field: d.fields?.[0] ?? '', from: '', to: '' }],
  }));

  const handleSave = () => {
    if (!draft.key?.trim()) return message.error('A merge key is required');
    if (!/^[a-z0-9_]+$/.test(draft.key)) return message.error('Key: lowercase letters, numbers, underscores only');
    if (!(draft.fields ?? []).length) return message.error('Pick at least one source field');
    const { mapRows, ...rest } = draft;
    onSave({ ...rest, valueMaps: buildValueMaps(mapRows) });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={820}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><MergeCellsOutlined /> {merge ? 'Edit Merged Field' : 'New Merged Field'}</span>}
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button>
        </div>
      }
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Merged Field</div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Key</Text>
            <Input size="small" placeholder="experience_range" value={draft.key ?? ''} onChange={(e) => set('key', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Label</Text>
            <Input size="small" placeholder="Experience" value={draft.label ?? ''} onChange={(e) => set('label', e.target.value)} />
          </div>
        </div>

        <div style={{ margin: '12px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Source fields (combined in order)</Text>
          <Select
            mode="multiple"
            size="small"
            style={{ width: '100%' }}
            placeholder="Pick fields to combine"
            options={fieldOptions}
            value={draft.fields ?? []}
            onChange={(v) => set('fields', v)}
            optionFilterProp="label"
          />
        </div>

        {/* Template — full control over layout & literals. Overrides Separator. */}
        <div style={{ margin: '12px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Template <span style={{ color: '#94a3b8' }}>(optional — overrides separator)</span>
          </Text>
          <Input.TextArea
            rows={2}
            value={draft.template ?? ''}
            onChange={(e) => set('template', e.target.value)}
            placeholder="{clientBudgetStart} - {clientBudgetEnd} {clientBudgetCurrency}/{clientBudgetUnit}"
            className="fg-mono-input"
          />
          {(draft.fields ?? []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              {(draft.fields ?? []).map((f) => (
                <Tag
                  key={f}
                  color="blue"
                  style={{ cursor: 'pointer', margin: 0 }}
                  onClick={() => set('template', `${draft.template ?? ''}{${f}}`)}
                >
                  + {`{${f}}`}
                </Tag>
              ))}
            </div>
          )}
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            Click a chip to insert a field. Everything else (spaces, “/”, “-”) is kept literally.
            Result e.g. <code>60 - 80 $/Monthly</code>.
          </Text>
        </div>

        {/* Per-field value maps — e.g. Monthly → mon. Unmapped values pass through. */}
        <div style={{ margin: '12px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Value mappings (raw → display)</Text>
            <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={!(draft.fields ?? []).length} onClick={addRow}>
              Add
            </Button>
          </div>
          {(draft.mapRows ?? []).map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
              <Select
                size="small"
                style={{ flex: 1.4 }}
                placeholder="field"
                value={row.field || undefined}
                options={(draft.fields ?? []).map((f) => ({ value: f, label: f }))}
                onChange={(v) => updateRow(i, { field: v })}
              />
              <Input size="small" style={{ flex: 1 }} placeholder="Monthly" value={row.from} onChange={(e) => updateRow(i, { from: e.target.value })} />
              <span style={{ color: '#94a3b8' }}>→</span>
              <Input size="small" style={{ flex: 1 }} placeholder="mon" value={row.to} onChange={(e) => updateRow(i, { to: e.target.value })} />
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(i)} />
            </div>
          ))}
          {!(draft.mapRows ?? []).length && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              e.g. Monthly → mon, Daily → day, Yearly → yr. Unmapped values stay as-is.
            </Text>
          )}
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Separator <span style={{ color: '#94a3b8' }}>(used when no template)</span></Text>
            <Input size="small" placeholder=" - " value={draft.separator ?? ''} onChange={(e) => set('separator', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Place in group</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              allowClear
              placeholder="(first source's group)"
              options={groupOptions}
              value={draft.group || undefined}
              onChange={(v) => set('group', v ?? '')}
            />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Prefix</Text>
            <Input size="small" value={draft.prefix ?? ''} onChange={(e) => set('prefix', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Suffix</Text>
            <Input size="small" placeholder="yrs" value={draft.suffix ?? ''} onChange={(e) => set('suffix', e.target.value)} />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Colour</Text>
            <ColorInput value={draft.color} onChange={(v) => set('color', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Capitalize</Text>
            <Select size="small" style={{ width: '100%' }} options={CAP_OPTIONS} value={capToValue(draft.capitalize)} onChange={(v) => set('capitalize', valueToCap(v))} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={draft.hideSources !== false} onChange={(v) => set('hideSources', v)} />
          <Text style={{ fontSize: 13 }}>Hide the source fields after merging</Text>
        </div>
      </div>
    </Modal>
  );
}

// ── Preview Drawer ──────────────────────────────────────────────────────────────
function PreviewDrawer({ open, module, onClose }) {
  const [recordId, setRecordId] = useState('');
  const [groups, setGroups] = useState([]);
  // Flattened record values, so the preview can resolve Conditional Labels the
  // same way the real detail view does.
  const [previewValues, setPreviewValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (!recordId.trim()) return message.warning('Enter a record _id to preview');
    setLoading(true); setError('');
    try {
      // Load saved display defaults alongside the record so the preview renders
      // with the configured S3 base / date format / labels.
      const [data] = await Promise.all([
        getModuleDetailPreview(module, recordId.trim()),
        loadDetailDefaults(),
      ]);
      setGroups(data);
      setPreviewValues(recordValues(data));
      if (!data.length) setError('No groups returned — check the id and module.');
    } catch (e) {
      setError(e?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={<span className="fg-drawer-title"><EyeOutlined /> Detail Preview — {module}</span>}
      classNames={{ header: 'fg-drawer-header', body: 'fg-drawer-body' }}
    >
      <Space.Compact style={{ width: '100%', marginBottom: 14 }}>
        <Input
          placeholder="Paste a record _id (24-char ObjectId)"
          value={recordId}
          onChange={(e) => setRecordId(e.target.value)}
          onPressEnter={load}
          className="fg-mono-input"
        />
        <Button type="primary" loading={loading} onClick={load}>Load</Button>
      </Space.Compact>

      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {groups.map((g) => (
          <div key={g.id ?? g.name} style={{ marginBottom: 18 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>{g.label ?? g.name}</Text>
            {(g.fields ?? []).filter((f) => f.show === undefined || f.show === 1).map((f) => (
              <div key={f.field} style={{ display: 'flex', gap: 10, padding: '4px 0', borderBottom: '1px dashed #eee' }}>
                {/* Resolve the Conditional Label against the previewed record,
                    exactly as DetailViewV1 does, so the preview cannot show a
                    different label than the real detail view. */}
                <Text type="secondary" style={{ fontSize: 12, width: 150, flexShrink: 0 }}>
                  {detailFieldLabel(f, previewValues)}
                </Text>
                <FieldValue field={f} />
              </div>
            ))}
          </div>
        ))}
        {!loading && !groups.length && !error && (
          <Empty description="Load a record to preview its detail view." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    </Drawer>
  );
}

// The empty-text kinds surfaced in the Display Defaults drawer. Keys match the
// render types resolved by components/detail/fieldRenderEngine, and the
// placeholders are the built-in sentences from components/detail/emptyText.js,
// so the admin sees exactly what they would get by leaving the box blank.
// Region profiles offered in Display Defaults. These are KEYS into each form
// group's `regionRules` map — adding a market is a config edit, not a code one,
// so this list only has to name the ones an admin can pick today.
const REGION_OPTIONS = [
  { label: 'Common (no region profile)', value: '' },
  { label: 'United States (US)', value: 'US' },
  { label: 'United Kingdom (UK)', value: 'UK' },
  { label: 'India (IND)', value: 'IND' },
];

const EMPTY_TEXT_KINDS = [
  { key: 'default',  label: 'Any value',   placeholder: BUILT_IN_EMPTY_TEXTS.default },
  { key: 'group',    label: 'Empty group', placeholder: 'No <group name> added' },
  { key: 'document', label: 'Document',    placeholder: BUILT_IN_EMPTY_TEXTS.document },
  { key: 'date',     label: 'Date',        placeholder: BUILT_IN_EMPTY_TEXTS.date },
  { key: 'phone',    label: 'Phone',       placeholder: BUILT_IN_EMPTY_TEXTS.phone },
  { key: 'email',    label: 'Email',       placeholder: BUILT_IN_EMPTY_TEXTS.email },
  { key: 'link',     label: 'Link',        placeholder: BUILT_IN_EMPTY_TEXTS.link },
  { key: 'skills',   label: 'Skills/tags', placeholder: BUILT_IN_EMPTY_TEXTS.skills },
];

// ── Display Defaults Drawer ─────────────────────────────────────────────────────
// Tenant-wide settings that used to be hard-coded in fileUtils.js / FieldValue.jsx.
function pickDefaults(d) {
  const out = {};
  ['s3BaseUrl', 'dateFormat', 'dateTimeFormat', 'phoneFormat', 'booleanTrueLabel', 'booleanFalseLabel', 'emptyPlaceholder', 'separator'].forEach((k) => {
    if (d?.[k] !== undefined && d?.[k] !== '') out[k] = d[k];
  });
  // phoneCountryCode is the one default where EMPTY is a deliberate setting
  // ("prefix nothing unless the record says so"), not "unset — keep the
  // built-in", so it is picked up whenever the server sent it as a string.
  if (typeof d?.phoneCountryCode === 'string') out.phoneCountryCode = d.phoneCountryCode;
  if (Array.isArray(d?.documentNameOrder) && d.documentNameOrder.length) out.documentNameOrder = d.documentNameOrder;
  if (d?.emptyTexts && typeof d.emptyTexts === 'object') out.emptyTexts = { ...d.emptyTexts };
  // Like phoneCountryCode, EMPTY is a real setting here ("use the viewer's own
  // zone"), so it is picked up whenever the server sent it as a string.
  if (typeof d?.timeZone === 'string') out.timeZone = d.timeZone;
  if (typeof d?.region === 'string') out.region = d.region;
  // A saved `false` is a real setting ("never print the zone"), so it is picked
  // up whenever it is present as a boolean, not only when truthy.
  if (typeof d?.showTimeZoneLabel === 'boolean') out.showTimeZoneLabel = d.showTimeZoneLabel;
  return out;
}

function DefaultsDrawer({ open, onClose }) {
  const [draft, setDraft] = useState(() => ({ ...getDefaults() }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getDetailDefaults()
      .then((d) => { if (!cancelled) setDraft((prev) => ({ ...prev, ...pickDefaults(d) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Built once: enumerating and offsetting every IANA zone is not free, and the
  // list only changes when the alias map does.
  const timeZoneOptions = useMemo(
    () => listTimeZones(draft).map(({ value, label }) => ({ value, label })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.timeZoneAliases],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        s3BaseUrl: (draft.s3BaseUrl ?? '').trim(),
        dateFormat: (draft.dateFormat ?? '').trim(),
        dateTimeFormat: (draft.dateTimeFormat ?? '').trim(),
        showTimeZoneLabel: draft.showTimeZoneLabel !== false,
        phoneFormat: (draft.phoneFormat ?? '').trim(),
        // Always sent, including as '' — that is how an admin CLEARS a global
        // dial code they previously set.
        phoneCountryCode: (draft.phoneCountryCode ?? '').trim(),
        booleanTrueLabel: draft.booleanTrueLabel ?? '',
        booleanFalseLabel: draft.booleanFalseLabel ?? '',
        emptyPlaceholder: draft.emptyPlaceholder ?? '',
        emptyTexts: draft.emptyTexts ?? {},
        // Always sent, including as '' — that is how an admin clears the tenant
        // zone and returns everyone to their own browser clock.
        timeZone: (draft.timeZone ?? '').trim(),
        // Always sent, including as '' — that is how an admin clears the region
        // and returns every group to its own defaults.
        region: (draft.region ?? '').trim(),
        separator: draft.separator ?? '',
        documentNameOrder: Array.isArray(draft.documentNameOrder) ? draft.documentNameOrder : [],
      };
      await saveDetailDefaults(payload);
      applyDefaults(payload); // live-update the singleton so detail views reflect immediately
      message.success('Display defaults saved');
      onClose();
    } catch (e) {
      message.error(e?.message || 'Could not save defaults');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 12, display: 'block', marginBottom: 4 };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      title={<span className="fg-drawer-title"><SettingOutlined /> Display Defaults</span>}
      classNames={{ header: 'fg-drawer-header', body: 'fg-drawer-body' }}
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={() => setDraft({ ...BUILT_IN_DEFAULTS })}>Reset to built-in</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save Defaults</Button>
        </div>
      }
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Documents / Files</div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>S3 Base URL</Text>
          <Input size="small" placeholder={BUILT_IN_DEFAULTS.s3BaseUrl} value={draft.s3BaseUrl ?? ''} onChange={(e) => set('s3BaseUrl', e.target.value)} className="fg-mono-input" />
        </div>
        <div>
          <Text type="secondary" style={labelStyle}>Document name priority (first match wins)</Text>
          <Select
            mode="tags"
            size="small"
            style={{ width: '100%' }}
            placeholder="name, documentName, uploadedFileName…"
            value={draft.documentNameOrder ?? []}
            onChange={(v) => set('documentNameOrder', v)}
          />
        </div>
      </div>

      <div className="fg-form-section">
        <div className="fg-form-section-label">Value Formatting</div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>Date format (dayjs tokens)</Text>
          <Input size="small" placeholder="DD MMM YYYY" value={draft.dateFormat ?? ''} onChange={(e) => set('dateFormat', e.target.value)} className="fg-mono-input" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>Phone format (digit groups, e.g. 3-3-4 → 999-878-3413)</Text>
          <Input size="small" placeholder="3-3-4" value={draft.phoneFormat ?? ''} onChange={(e) => set('phoneFormat', e.target.value)} className="fg-mono-input" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>Fallback country code (leave blank — recommended)</Text>
          <Input size="small" placeholder="blank = no code" value={draft.phoneCountryCode ?? ''} onChange={(e) => set('phoneCountryCode', e.target.value)} className="fg-mono-input" />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            Used ONLY when a record carries no dial code of its own. It ships empty on
            purpose: a number that never stated a country is shown without one, rather
            than being stamped with a code someone assumed. Set it only if this tenant
            really is single-country.
          </Text>
        </div>
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Boolean — true</Text>
            <Input size="small" placeholder="Yes" value={draft.booleanTrueLabel ?? ''} onChange={(e) => set('booleanTrueLabel', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Boolean — false</Text>
            <Input size="small" placeholder="No" value={draft.booleanFalseLabel ?? ''} onChange={(e) => set('booleanFalseLabel', e.target.value)} />
          </div>
        </div>
        {/* The region profile. It carries no logic of its own: it selects which
            `regionRules` block each form group applies, so switching it
            re-renders every affected component with no code change. Used today
            for whether a candidate may submit an in-progress qualification
            (normal in the US/UK, not in India) and whether a lower degree is
            mandatory alongside a higher one. */}
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={labelStyle}>Region</Text>
          <Select
            size="small"
            allowClear
            style={{ width: '100%' }}
            placeholder="Common — every group uses its own defaults"
            value={draft.region || undefined}
            onChange={(v) => set('region', v ?? '')}
            options={REGION_OPTIONS}
            getPopupContainer={() => document.body}
          />
          <Text type="secondary" style={{ ...labelStyle, marginTop: 4 }}>
            Different markets expect different things. Choosing one here switches the rules
            that have been set up for it — for example whether someone may be put forward
            while still studying, which is normal in the US and UK but not in India. Leave
            it on Common if the same rules suit everyone.
          </Text>
        </div>

        {/* The application's clock. Every date-time is RENDERED in this zone and
            every datetime/time INPUT is interpreted as being in it — so a team
            spread across locations reads and enters the same wall-clock value.
            Plain calendar dates (date of birth, expiry, education dates) are
            deliberately NOT converted; see services/timezone.js. */}
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={labelStyle}>Application time zone</Text>
          <Select
            size="small"
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder="Use each viewer's own browser time zone"
            optionFilterProp="label"
            value={draft.timeZone || undefined}
            onChange={(v) => set('timeZone', v ?? '')}
            options={timeZoneOptions}
            getPopupContainer={() => document.body}
          />
          <Text type="secondary" style={{ ...labelStyle, marginTop: 4 }}>
            {draft.timeZone
              ? `Everyone now sees and enters times in ${tzLabel({ ...draft, timeZone: draft.timeZone })}, whichever country they are working from — so an interview slot means the same thing to all of them. Plain dates such as a date of birth are not affected.`
              : 'Not set — everyone sees times in their own computer\u2019s time zone, so the same interview can appear at two different times to two people.'}
          </Text>
        </div>

        {/* Two formats, not one. A date of birth has no time and no zone —
            printing "07:03 PM IST" after one claims a precision the value does
            not have. A created/updated stamp is a moment, so it carries both. */}
        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Date and time format</Text>
            <Input size="small" placeholder="MMM DD, YYYY | hh:mm A"
              value={draft.dateTimeFormat ?? ''}
              onChange={(e) => set('dateTimeFormat', e.target.value)} />
            <Text type="secondary" style={{ ...labelStyle, marginTop: 4 }}>
              Used wherever a moment is shown — when a record was created or last
              changed, activity entries, interview slots. Example:
              <b> {formatAppDateTime(SAMPLE_INSTANT, { ...draft, showTimeZoneLabel: draft.showTimeZoneLabel !== false }) || '—'}</b>
            </Text>
          </div>
          <div style={{ flex: '0 0 200px' }}>
            <Text type="secondary" style={labelStyle}>Show the time zone</Text>
            <div>
              <Switch
                size="small"
                checked={draft.showTimeZoneLabel !== false}
                onChange={(v) => set('showTimeZoneLabel', v)}
                checkedChildren="Shown"
                unCheckedChildren="Hidden"
              />
            </div>
            <Text type="secondary" style={{ ...labelStyle, marginTop: 4 }}>
              Adds a small &quot;{tzShortLabel(draft)}&quot; after every time, so nobody has to
              guess whose clock it is. Only times get it — plain dates never do.
            </Text>
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Empty placeholder</Text>
            <Input size="small" placeholder="-" value={draft.emptyPlaceholder ?? ''} onChange={(e) => set('emptyPlaceholder', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Object / array separator</Text>
            <Input size="small" placeholder=" - " value={draft.separator ?? ''} onChange={(e) => set('separator', e.target.value)} />
          </div>
        </div>

        {/* Per-kind empty text. "Empty placeholder" above can only ever say ONE
            thing, so every absence read as the same bare "-" — indistinguishable
            from a rendering fault. These say what is actually missing. Each is
            OPTIONAL: left blank, the built-in sentence for that kind is used. */}
        <div style={{ marginTop: 18 }}>
          <Text strong style={{ fontSize: 12 }}>Empty-value text by kind</Text>
          <Text type="secondary" style={{ ...labelStyle, marginTop: 2 }}>
            What people see where there is nothing to show. A dash tells them nothing —
            &quot;No experience added&quot; tells them the record is fine and that part is
            simply empty. Leave a box blank to use the built-in wording; a single field or
            section can still say something different.
          </Text>
          <div className="fg-field-meta-row" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            {EMPTY_TEXT_KINDS.map(({ key, label, placeholder }) => (
              <div key={key} style={{ flex: '1 1 190px' }}>
                <Text type="secondary" style={labelStyle}>{label}</Text>
                <Input
                  size="small"
                  placeholder={placeholder}
                  value={draft.emptyTexts?.[key] ?? ''}
                  onChange={(e) => set('emptyTexts', { ...(draft.emptyTexts ?? {}), [key]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function DetailGroupsSection() {
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [selected, setSelected] = useState('');

  const [groups, setGroups] = useState([]); // raw form-groups (listed like FormGroups)
  const [fields, setFields] = useState([]); // flattened — used for merge field options
  const [groupNames, setGroupNames] = useState([]);
  const [fieldCfg, setFieldCfg] = useState({}); // { [fieldKey]: override }
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [editField, setEditField] = useState(null); // field meta being edited
  const [mergeDraft, setMergeDraft] = useState(null); // { index, merge } | { index:null }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [gate, setGate] = useState(null); // OTP-gated config action meta
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState('');
  // Client scope, mirroring the Form Groups admin: blank edits the module
  // default, a client edits that client's own detail config.
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  // Which scope the LOADED config actually came from. When a client is selected
  // but has no config of its own the backend serves the default, and the admin
  // needs to know that what they see is inherited, not client-specific.
  const [loadedClientId, setLoadedClientId] = useState('');
  const [groupHideSaving, setGroupHideSaving] = useState({}); // { [groupId]: true } while a hideGroup PUT is in flight

  function openGate(action) { setGate(CONFIG_GATE_META[action]); }

  // Both visibility flags live on the formGroupConfig group document itself
  // (the same one Form Groups edits) — flipping either writes straight through
  // via updateAdminFormGroup. `hideGroup` hides the group EVERYWHERE (forms +
  // detail, shared with Form Groups); `hideGroupInDetail` hides it from the
  // DETAIL VIEW only, leaving the add/edit forms untouched — the group-level
  // counterpart of the per-field "Hide in the detail view only" override.
  async function toggleGroupFlag(group, flag, value, messages) {
    const previous = groups;
    setGroups((cur) => cur.map((g) => (g.id === group.id ? { ...g, [flag]: value } : g)));
    setGroupHideSaving((cur) => ({ ...cur, [`${group.id}:${flag}`]: true }));
    try {
      await updateAdminFormGroup(group.id, { ...group, [flag]: value });
      message.success(value ? messages.on : messages.off);
    } catch (e) {
      setGroups(previous);
      message.error(e?.message || 'Could not update group visibility');
    } finally {
      setGroupHideSaving((cur) => {
        const next = { ...cur };
        delete next[`${group.id}:${flag}`];
        return next;
      });
    }
  }

  // Free-text counterpart of toggleGroupFlag, for the group's empty-state text.
  // Kept separate because it saves on BLUR rather than on every keystroke —
  // one PUT per character would hammer the API and race itself.
  async function saveGroupText(group, key, value) {
    const next = String(value ?? '').trim();
    if (next === String(group?.[key] ?? '').trim()) return;
    const previous = groups;
    setGroups((cur) => cur.map((g) => (g.id === group.id ? { ...g, [key]: next } : g)));
    try {
      await updateAdminFormGroup(group.id, { ...group, [key]: next });
      message.success('Empty text saved');
    } catch (e) {
      setGroups(previous);
      message.error(e?.message || 'Could not save empty text');
    }
  }

  const toggleGroupHidden = (group, hidden) => toggleGroupFlag(group, 'hideGroup', hidden, {
    on: 'Group hidden from add/edit forms and the detail view',
    off: 'Group is visible again',
  });

  const toggleGroupDetailHidden = (group, hidden) => toggleGroupFlag(group, 'hideGroupInDetail', hidden, {
    on: 'Group hidden from the detail view only — add/edit forms keep it',
    off: 'Group is visible in the detail view again',
  });

  // The detail view honours the same Show Condition the forms do, so a group
  // that no longer applies to the record is hidden instead of showing a stale
  // value. This switch opts a group out of that, keeping it always visible.
  const toggleGroupIgnoreShowIf = (group, ignore) => toggleGroupFlag(group, 'ignoreShowIfInDetail', ignore, {
    on: 'Group always shows in the detail view, even when its Show Condition fails',
    off: 'Group follows its Show Condition in the detail view',
  });

  // After the OTP modal verifies + performs the action, re-fetch the selected
  // module's config (bump refreshKey, which the loader effect depends on).
  function handleGateVerified() {
    setGate(null);
    setRefreshKey((k) => k + 1);
  }

  useEffect(() => {
    getAdminClients({ limit: 200 })
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    getAvailableModules()
      .then((raw) => {
        const mods = normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
        setModules(mods);
        if (mods.length) setSelected(mods[0].key);
      })
      .catch(() => setModules([]))
      .finally(() => setModulesLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const [groupData, cfg] = await Promise.all([
          getAdminFormGroups(selected, { clientId }),
          getDetailConfig(selected, { clientId }),
        ]);
        if (cancelled) return;
        setLoadedClientId(cfg.clientId ?? '');
        setGroups(groupData ?? []);
        setFields(flattenFields(groupData));
        setGroupNames((groupData ?? []).map((g) => ({ value: g.name, label: g.label ?? g.name })));
        const map = {};
        (cfg.fields ?? []).forEach((fc) => { map[fc.field] = fc; });
        setFieldCfg(map);
        setMerges(cfg.merges ?? []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, refreshKey, clientId]);

  const fieldOptions = useMemo(() => fields.map((f) => ({ value: f.key, label: `${f.label} (${f.key})` })), [fields]);

  async function handleSave() {
    setSaving(true);
    try {
      const fieldsPayload = Object.entries(fieldCfg).map(([k, c]) => cleanFieldCfg(k, c)).filter(Boolean);
      const { nextGroups, changed } = syncGroupsWithDetailVisibility(groups, fieldCfg);
      await saveDetailConfig({ module: selected, fields: fieldsPayload, merges: cleanMerges(merges), clientId });
      if (changed.length) {
        setGroups(nextGroups);
        setFields(flattenFields(nextGroups));
        setGroupNames(nextGroups.map((g) => ({ value: g.name, label: g.label ?? g.name })));
      }
      // Saving against a client CREATES that client's config on first write, so
      // reflect the new scope instead of leaving the "inherited" hint up.
      setLoadedClientId(clientId);
      message.success(clientId
        ? 'Detail view configuration saved for this client'
        : 'Detail view configuration saved');
    } catch (e) {
      message.error(e?.message || 'Could not save configuration');
    } finally {
      setSaving(false);
    }
  }

  function applyFieldOverride(draft) {
    setFieldCfg((prev) => ({ ...prev, [editField.key]: { ...draft, field: editField.key } }));
    setEditField(null);
  }

  function clearFieldOverride(key) {
    setFieldCfg((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function applyMerge(draft) {
    setMerges((prev) => {
      const next = [...prev];
      if (mergeDraft.index === null) next.push(draft);
      else next[mergeDraft.index] = draft;
      return next;
    });
    setMergeDraft(null);
  }

  // Quick inline hidden toggle
  function toggleHidden(key, hidden) {
    setFieldCfg((prev) => {
      const cur = prev[key] ?? { field: key };
      return { ...prev, [key]: { ...cur, hidden } };
    });
  }

  const overrideTags = (cfg) => {
    if (!cfg) return null;
    const tags = [];
    if (cfg.label) tags.push(<Tag key="l" color="blue">label</Tag>);
    if (cfg.prefix || cfg.suffix) tags.push(<Tag key="ps" color="geekblue">±fix</Tag>);
    if (cfg.color) tags.push(<Tag key="c" color={cfg.color}>colour</Tag>);
    if (cfg.capitalize === true) tags.push(<Tag key="cap" color="purple">Title</Tag>);
    if (cfg.capitalize === false) tags.push(<Tag key="cap" color="default">raw</Tag>);
    if (cfg.lookupCollection) tags.push(<Tag key="lk" color="volcano">lookup: {cfg.lookupCollection}</Tag>);
    if (cfg.ignoreLabelWhenInDetail) tags.push(<Tag key="ilw" color="default">static label</Tag>);
    const rt = cfg.renderType || (cfg.documentPreview ? 'document' : cfg.displayAs);
    if (rt && rt !== 'auto') tags.push(<Tag key="rt" color="cyan">{rt}</Tag>);
    return tags.length ? tags : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  };

  // Groups listed exactly like FormGroupsSection (same getAdminFormGroups API),
  // each row expandable to its fields — but the per-field action configures the
  // DETAIL override instead of editing the group.
  const groupColumns = [
    {
      title: 'Group',
      dataIndex: 'label',
      key: 'label',
      render: (label, g) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{label ?? g.name}</Text>,
    },
    { title: 'Key', dataIndex: 'name', key: 'name', render: (name) => <code className="fg-table-code">{name}</code> },
    { title: 'Order', dataIndex: 'order', key: 'order', width: 72, align: 'center', render: (v) => <span className="fg-order-cell">{v ?? 0}</span> },
    {
      title: 'Fields', dataIndex: 'fields', key: 'fields', width: 76, align: 'center',
      render: (f) => <Badge count={f?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: 'Customized', key: 'cust', width: 100, align: 'center',
      render: (_, g) => {
        const n = (g.fields ?? []).filter((f) => fieldCfg[f.field]).length;
        return n ? <Tag color="blue" style={{ margin: 0 }}>{n}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
      },
    },
    {
      title: 'Hide Group', key: 'hideGroup', width: 110, align: 'center',
      render: (_, g) => (
        <Tooltip title={truthy(g.hideGroup) ? 'Hidden from add/edit forms AND the detail view' : 'Visible in both — same flag as Form Groups'}>
          <Switch
            size="small"
            checkedChildren="Hide"
            unCheckedChildren="Show"
            checked={truthy(g.hideGroup)}
            loading={Boolean(groupHideSaving[`${g.id}:hideGroup`])}
            onChange={(v) => toggleGroupHidden(g, v)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Show Condition', key: 'ignoreShowIfInDetail', width: 130, align: 'center',
      render: (_, g) => {
        const cond = g.showIf?.field
          ? `${g.showIf.field} ${g.showIf.operator ?? 'eq'} ${g.showIf.value ?? ''}`.trim()
          : null;
        if (!cond) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Tooltip title={truthy(g.ignoreShowIfInDetail)
            ? `Always shown, ignoring "${cond}"`
            : `Hidden in the detail view unless "${cond}"`}>
            <Switch
              size="small"
              checkedChildren="Always"
              unCheckedChildren="Follow"
              checked={truthy(g.ignoreShowIfInDetail)}
              loading={Boolean(groupHideSaving[`${g.id}:ignoreShowIfInDetail`])}
              onChange={(v) => toggleGroupIgnoreShowIf(g, v)}
            />
          </Tooltip>
        );
      },
    },
    {
      // Only meaningful for a repeatable group: it is what shows INSTEAD of an
      // empty card when the record has no rows. A non-addRow group renders its
      // fields' own empty texts, so the column stays blank for those.
      title: 'Empty Text', key: 'emptyText', width: 220,
      render: (_, g) => (truthy(g.addRow) ? (
        <Input
          size="small"
          defaultValue={g.emptyText ?? ''}
          placeholder={`No ${String(g.label ?? g.name ?? '').toLowerCase()} added`}
          onBlur={(e) => saveGroupText(g, 'emptyText', e.target.value)}
          onPressEnter={(e) => e.currentTarget.blur()}
        />
      ) : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>),
    },
    {
      title: 'Hide in Detail', key: 'hideGroupInDetail', width: 120, align: 'center',
      render: (_, g) => (
        <Tooltip title={truthy(g.hideGroupInDetail)
          ? 'Hidden from the DETAIL VIEW only — the add/edit forms still show this group'
          : 'Shown in the detail view. Hide here to remove it from the detail view without touching the forms'}>
          <Switch
            size="small"
            checkedChildren="Hide"
            unCheckedChildren="Show"
            checked={truthy(g.hideGroupInDetail)}
            loading={Boolean(groupHideSaving[`${g.id}:hideGroupInDetail`])}
            onChange={(v) => toggleGroupDetailHidden(g, v)}
          />
        </Tooltip>
      ),
    },
  ];

  // Expanded rows: the fields inside a group, each with its detail-override
  // controls (visibility toggle, customize, reset).
  const renderGroupFields = (group) => {
    const fieldsArr = group.fields ?? [];
    if (!fieldsArr.length) {
      return <Text type="secondary" style={{ fontSize: 12 }}>No fields in this group.</Text>;
    }
    return (
      <div className="fg-expanded-fields">
        {fieldsArr.map((f) => {
          const cfg = fieldCfg[f.field];
          const hidden = cfg?.hidden ?? fieldShowToHidden(f);
          const meta = {
            key: f.field,
            label: f.label ?? f.field,
            type: f.type ?? 'text',
            group: group.label ?? group.name,
            // Carried through so the drawer can show the Conditional Label rule
            // and offer the detail-only opt-out.
            labelWhen: f.labelWhen,
            lookupDefaults: f.lookupCollection ? {
              lookupCollection: f.lookupCollection,
              displayField: f.displayField ?? '',
              displayField2: f.displayField2 ?? '',
              valueField: f.valueField || '_id',
            } : {},
          };
          return (
            <div key={f.field} className="fg-field-row">
              <div className="fg-field-row-info">
                <span className="fg-field-row-label">{cfg?.label || f.label}</span>
                <span className="fg-field-row-key">{f.field}</span>
                <span className="fg-field-row-type">({f.type})</span>
                {/* Read-only marker for a uniqueness rule authored in Form
                    Groups. It is informational ONLY: Detail Groups never runs a
                    uniqueness check, never edits the rule, and nothing saved
                    here can override or remove it. */}
                {hasUniqueValidation(f) && (
                  <Tooltip title={(() => {
                    const rule = uniqueValidationOf(f);
                    const stored = rule?.value?.collectionField || f.payloadKey || f.field;
                    return `Configured unique in Form Groups on "${stored}"`
                      + `${rule?.message ? ` — "${rule.message}"` : ''}. Edit it there; this screen is display only.`;
                  })()}>
                    <Tag color="magenta" style={{ marginLeft: 6 }}>Unique</Tag>
                  </Tooltip>
                )}
                {/* Effective conditional labels: rules authored here win over
                    the inherited Form Groups rule, matching detailLabelRules. */}
                {(() => {
                  const own = (cfg?.labelWhen ?? []).filter((r) => r?.field && r?.label);
                  const rules = own.length
                    ? own
                    : (f.labelWhen?.field && f.labelWhen?.label ? [f.labelWhen] : []);
                  if (!rules.length) return null;
                  const ignored = cfg?.ignoreLabelWhenInDetail;
                  const describe = (r) => `"${r.label}" when ${r.field} ${r.operator ?? 'eq'} ${r.value ?? ''}`.trim();
                  return (
                    <Tooltip title={ignored
                      ? `Conditional labels ignored here — the detail view always shows "${f.label}"`
                      : `${own.length ? 'Set in Detail Groups' : 'Inherited from Form Groups'} — ${rules.map(describe).join('; ')}`}>
                      <Tag color={ignored ? 'default' : own.length ? 'purple' : 'blue'} style={{ marginLeft: 6 }}>
                        ⇄ {rules[0].label}{rules.length > 1 ? ` +${rules.length - 1}` : ''}
                      </Tag>
                    </Tooltip>
                  );
                })()}
              </div>
              <div className="fg-field-row-flags">{overrideTags(cfg)}</div>
              <Space size={4} style={{ marginLeft: 'auto' }}>
                <Tooltip title={hidden ? 'Hidden in form and detail view' : 'Visible'}>
                  <Switch size="small" checkedChildren="Hide" unCheckedChildren="Show" checked={hidden} onChange={(v) => toggleHidden(f.field, v)} />
                </Tooltip>
                <Tooltip title="Customize field"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditField(meta)} /></Tooltip>
                {cfg && <Tooltip title="Reset to default"><Button type="text" size="small" danger icon={<ReloadOutlined />} onClick={() => clearFieldOverride(f.field)} /></Tooltip>}
              </Space>
            </div>
          );
        })}
      </div>
    );
  };

  const sortedGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return [...groups]
      .filter((group) => !needle || [
        group.label,
        group.name,
        ...(group.fields ?? []).flatMap((field) => [field.label, field.field, field.type]),
      ].some((value) => String(value ?? '').toLowerCase().includes(needle)))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [groups, query]);

  const mergeColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label', render: (l, r) => <span><Text strong>{l || r.key}</Text><span className="fg-field-key-hint">{r.key}</span></span> },
    { title: 'Fields', dataIndex: 'fields', key: 'fields', render: (f) => <Space size={4} wrap>{(f ?? []).map((x) => <Tag key={x}>{x}</Tag>)}</Space> },
    {
      title: 'Format', key: 'sep', width: 220,
      render: (_, r) => <code className="fg-table-code">{r.template?.trim() ? r.template : `join: ${r.separator || ' - '}`}</code>,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_, r, i) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => setMergeDraft({ index: i, merge: r })} /></Tooltip>
          <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => setMerges((prev) => prev.filter((_, idx) => idx !== i))} /></Tooltip>
        </Space>
      ),
    },
  ];

  const moduleTabs = modules.map((m) => ({ key: m.key, label: m.label }));

  return (
    <div className="fg-section">
      <AppCard
        icon={<BgColorsOutlined />}
        title="Detail Groups"
        extra={
          <Space size={8} wrap>
            <Dropdown trigger={['click']} menu={{ items: [
              { key: 'save', label: 'Save current as default', icon: <SaveOutlined />, onClick: () => openGate('save') },
              { key: 'restore', label: 'Restore saved default', icon: <CloudDownloadOutlined />, onClick: () => openGate('restore') },
              { type: 'divider' },
              { key: 'reset', label: 'Reset all detail settings', icon: <ReloadOutlined />, danger: true, onClick: () => openGate('reset') },
            ] }}>
              <Button size="small" icon={<MoreOutlined />}>Workspace defaults</Button>
            </Dropdown>
            <Button size="small" icon={<SettingOutlined />} onClick={() => setDefaultsOpen(true)}>Display Defaults</Button>
            <Button size="small" icon={<EyeOutlined />} disabled={!selected} onClick={() => setPreviewOpen(true)}>Preview</Button>
            <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} disabled={!selected} onClick={handleSave}>
              Save Configuration
            </Button>
          </Space>
        }
      >
        <div className="admin-accent-bar" />
        <div className="fg-scope-toolbar" style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Select a client to edit a client-scoped detail view for this module. Leave blank for the
            module default. A record belonging to a client automatically uses that client&apos;s
            detail config, falling back to the default when the client has none.
          </Text>
          <Space wrap size={8}>
            <Select
              size="small"
              allowClear
              showSearch
              placeholder="Client (default)"
              style={{ width: 240 }}
              value={clientId || undefined}
              optionFilterProp="label"
              options={clients
                .map((c) => ({
                  value: c._id ?? c.id,
                  label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                }))
                .filter((c) => c.value)}
              onChange={(next) => setClientId(next ?? '')}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => setRefreshKey((k) => k + 1)}>
              Reload
            </Button>
          </Space>
        </div>
        {clientId && !loadedClientId && !loading && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="This client has no detail config of its own yet"
            description="You are viewing the module default. Saving now creates a config for this client only — the default and every other client are left untouched."
          />
        )}
        <Text type="secondary" style={{ fontSize: 13 }}>
          Customize how each module&apos;s detail view renders: per-field label, prefix/suffix, colour,
          capitalization, hide, reference lookups (id → name), and merged display fields. These overrides
          are applied on top of Form Groups and the add/edit forms are unaffected — except &quot;Hide Group&quot;,
          which is the same flag Form Groups uses, so hiding a group here (or there) hides it in both places.
          &quot;Hide in Detail&quot; hides the group from the detail view only, leaving the forms untouched.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { paddingTop: 8 } }}>
        <Spin spinning={modulesLoading}>
          <Tabs activeKey={selected} onChange={setSelected} items={moduleTabs.map((t) => ({ ...t, children: null }))} className="admin-module-tabs" />
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Spin spinning={loading}>
            <div className="admin-config-table-tools">
              <Input allowClear prefix={<SearchOutlined />} placeholder="Search groups or fields" value={query} onChange={(event) => setQuery(event.target.value)} />
              <Text type="secondary" style={{ fontSize: 12 }}>{groups.length} groups · {fields.length} fields</Text>
            </div>
            <Table
              rowKey={(g) => g.id ?? g.name}
              dataSource={sortedGroups}
              columns={groupColumns}
              pagination={false}
              size="small"
              className="fg-table"
              expandable={{ expandedRowRender: renderGroupFields, rowExpandable: () => true }}
              locale={{ emptyText: <Empty description="Select a module to see its groups and fields." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />

            <div style={{ margin: '20px 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong><MergeCellsOutlined /> Merged Fields</Text>
              <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={!selected} onClick={() => setMergeDraft({ index: null, merge: null })}>
                Add Merge
              </Button>
            </div>
            <Table
              rowKey="key"
              dataSource={merges}
              columns={mergeColumns}
              pagination={false}
              size="small"
              className="fg-table"
              locale={{ emptyText: <Empty description="No merged fields. Combine e.g. 'from' + 'to' into one." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Spin>
        </Spin>
      </AppCard>

      <FieldOverrideDrawer
        key={editField?.key ?? 'field-closed'}
        open={!!editField}
        fieldMeta={editField}
        value={editField ? { ...editField.lookupDefaults, ...(fieldCfg[editField.key] ?? {}) } : null}
        moduleFieldOptions={fieldOptions}
        onClose={() => setEditField(null)}
        onSave={applyFieldOverride}
      />

      <MergeDrawer
        key={mergeDraft ? `merge-${mergeDraft.index ?? 'new'}` : 'merge-closed'}
        open={!!mergeDraft}
        merge={mergeDraft?.merge}
        fieldOptions={fieldOptions}
        groupOptions={groupNames}
        onClose={() => setMergeDraft(null)}
        onSave={applyMerge}
      />

      <DefaultsDrawer open={defaultsOpen} onClose={() => setDefaultsOpen(false)} />

      <PreviewDrawer open={previewOpen} module={selected} onClose={() => setPreviewOpen(false)} />

      <OtpGateModal
        open={!!gate}
        scope="detailConfig"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
